<template>
  <main-layout
    customControls
    controlsBg="light"
    :hasControlsBar="false"
    :breadcrumb="getBreadcrumb">
    <div class="container">
      <grid-row class="margin-top-xl" column-align="center">
        <grid-col col-size="4">
          <div id="header">
            <h1 class="header-title">
              <chevron
                @click="$router.push({name: 'Workspace'})"/>
              {{ $t('health.menu.nutritionalPlanTest') }}
            </h1>
          </div>
        </grid-col>
      </grid-row>
      <grid-row class="margin-top-m" column-align="center" col-spaced>
        <grid-col col-size="1">
          <label-input
            v-model="objectiveKcals"
            label="Objective Kcals"
            type="number"
          />
        </grid-col>
        <grid-col col-size="1">
          <p class="checkbox-label">{{$t('health.nutritionalPlan.prototype.addBreakfastSnack')}}</p>
          <checkbox v-on:change="updateBreakfastSnack"
                    id='addBreakfastSnack'
                    :checked="addBreakfastSnack"
                    check-size="m"/>
        </grid-col>
        <grid-col col-size="1">
          <p class="checkbox-label">{{$t('health.nutritionalPlan.prototype.addLunchSnack')}}</p>
          <checkbox v-on:change="updateLunchSnack"
                    id='addLunchSnack'
                    :checked="addLunchSnack"
                    check-size="m"/>
        </grid-col>
        <grid-col col-size="1">
          <btn-pill
            class="margin-top-xl"
            :btnText="$t('health.nutritionalPlan.prototype.recalculate')"
            v-on:click="getPlans"
            btn-size="s"/>
        </grid-col>
      </grid-row>
      <grid-row class="margin-top-xl" column-align="center">
        <grid-col col-size="1">
          <h3>{{$t('health.nutritionalPlan.prototype.daysOfPlan')}}: {{plans.length}}</h3>
        </grid-col>
      </grid-row>
      <div v-for="(plan, index) in plans" :key="index" col-spaced class="margin-bottom-xl">
        <grid-row class="margin-top-xxl" column-align="center">
          <grid-col col-size="1">
            <h3>{{getWeekDay(index)}}</h3>
          </grid-col>
        </grid-row>
        <grid-row class="margin-top-xl" column-align="center">
          <grid-col col-size="4">
            <grid-row column-align="center" col-spaced>
              <grid-col col-size="1">
                <h4>{{$t('health.nutritionalPlan.prototype.breakfast')}}:</h4>
                <p class="margin-right-s" v-if="!addBreakfastSnack && plan.snackBreakfast != null">{{ plan.snackBreakfast.recipeName }}</p>
                <p class="margin-right-s">{{ plan.breakfast.recipeName }}</p>
                <ingest-macro-distribution-chart
                  :ingredients="getBreakfastIngredients(plan)"
                  />
              </grid-col>
              <grid-col col-size="1">
                <h4>{{$t('health.nutritionalPlan.prototype.lunch')}}:</h4>
                <p class="margin-right-s" v-if="!addLunchSnack && plan.snackLunch != null">{{ plan.snackLunch.recipeName }}</p>
                <p class="margin-right-s">{{ plan.lunch.recipeName }}</p>
                <ingest-macro-distribution-chart
                  :ingredients="getLunchIngredients(plan)"
                />
              </grid-col>
              <grid-col col-size="1">
                <h4>{{$t('health.nutritionalPlan.prototype.dinner')}}:</h4>
                <p class="margin-right-s">{{ plan.dinner.recipeName }}</p>
                <ingest-macro-distribution-chart
                  :ingredients="plan.dinner.ingredients"
                />
              </grid-col>
            </grid-row>
            <grid-row column-align="center" col-spaced v-if="addLunchSnack || addBreakfastSnack">
              <grid-col col-size="1">
                <h4>{{$t('health.nutritionalPlan.prototype.breakfastSnack')}}:</h4>
                <div v-if="plan.snackBreakfast != null && addBreakfastSnack">
                  <p class="margin-right-s">{{ plan.snackBreakfast.recipeName }}</p>
                  <ingest-macro-distribution-chart
                    :ingredients="plan.snackBreakfast.ingredients"
                  />
                </div>
                <div v-else>
                  <p class="margin-right-s">--</p>
                </div>
              </grid-col>
              <grid-col col-size="1">
                <h4>{{$t('health.nutritionalPlan.prototype.lunchSnack')}}:</h4>
                <div v-if="plan.snackLunch != null && addLunchSnack">
                  <p class="margin-right-s">{{ plan.snackLunch.recipeName }}</p>
                  <ingest-macro-distribution-chart
                    :ingredients="plan.snackLunch.ingredients"
                  />
                </div>
                <div v-else>
                  <p class="margin-right-s">--</p>
                </div>
              </grid-col>
            </grid-row>
          </grid-col>
          <grid-col col-size="1" class="center-vertical">
            <h4>{{$t('health.nutritionalPlan.prototype.total')}}:</h4>
            <ingest-macro-distribution-chart
              :ingredients="getAllIngredientsForDay(plan)"
              :width="300"
              :height="300"
            />
          </grid-col>
        </grid-row>
      </div>
    </div>
    <alert
      v-if="alertVisible"
      :message="alertMessage"
      :type="alertType">
    </alert>
  </main-layout>
</template>

<script>
import MainLayout from '@/layouts//MainLayout';
import GridRow from '@/components/shared/grid/GridRow';
import GridCol from '@/components/shared/grid/GridCol';
import Chevron from '@/components/shared/Chevron';
import LabelInput from '@/components/shared/forms/LabelInput';
import api from 'BRAND/api/nutritionPlan';
import BtnPill from '@/components/shared/buttons/BtnPill';
import Alert from '@/components/shared/alert/Alert';
import IngestMacroDistributionChart from 'BRAND/components/nutritionPlan/IngestMacroDistributionChart';
import Checkbox from '@/components/shared/forms/Checkbox';

export default {
  name: 'NutritionalPlanPrototypeTester',
  components: {
    BtnPill,
    MainLayout,
    Chevron,
    GridRow,
    GridCol,
    LabelInput,
    Alert,
    IngestMacroDistributionChart,
    Checkbox
  },
  data () {
    return {
      objectiveKcals: '2000',
      plans: [],
      addBreakfastSnack: false,
      addLunchSnack: false,
      /* Alert */
      alertVisible: false,
      alertMessage: '',
      alertType: ''
    };
  },
  mounted () {
    this.getPlans();
  },
  computed: {
    getBreadcrumb () {
      return [
        {name: this.$t('menu.workspace'), link: '/workspace'},
        {name: this.$t('health.menu.nutritionalPlanTest'), disabled: true}
      ];
    }
  },
  methods: {
    getPlans () {
      this.plans = [];
      api.getNutritionalPlan(this, Number(this.objectiveKcals), this.addBreakfastSnack, this.addLunchSnack)
        .then(response => {
          this.plans = response;
        })
        .catch(() => {
          showAlert(this, this.$t('health.nutritionalPlan.prototype.noNutritionalPlanResult'), 'error');
        });
    },
    getAllIngredientsForDay (day) {
      const ingredients = [];
      if (day.breakfast != null) {
        ingredients.push(...day.breakfast.ingredients);
      }
      if (day.snackBreakfast != null) {
        ingredients.push(...day.snackBreakfast.ingredients);
      }
      if (day.lunch != null) {
        ingredients.push(...day.lunch.ingredients);
      }
      if (day.snackLunch != null) {
        ingredients.push(...day.snackLunch.ingredients);
      }
      if (day.dinner != null) {
        ingredients.push(...day.dinner.ingredients);
      }
      return ingredients;
    },
    getWeekDay (index) {
      const values = this.$t('health.nutritionalPlan.prototype.weekdays');
      return values[index];
    },
    updateBreakfastSnack (row) {
      this.addBreakfastSnack = !this.addBreakfastSnack;
    },
    updateLunchSnack (row) {
      this.addLunchSnack = !this.addLunchSnack;
    },
    getBreakfastIngredients (plan) {
      const ingredients = [];
      if (plan.breakfast != null) {
        ingredients.push(...plan.breakfast.ingredients);
      }
      if (!this.addBreakfastSnack && plan.snackBreakfast != null) {
        ingredients.push(...plan.snackBreakfast.ingredients);
      }
      return ingredients;
    },
    getLunchIngredients (plan) {
      const ingredients = [];
      if (plan.lunch != null) {
        ingredients.push(...plan.lunch.ingredients);
      }
      if (!this.addLunchSnack && plan.snackLunch != null) {
        ingredients.push(...plan.snackLunch.ingredients);
      }
      return ingredients;
    }
  }
};

const showAlert = (context, message, type) => {
  context.alertVisible = true;
  context.alertMessage = message;
  context.alertType = type;
  context.showAlert = true;
  setTimeout(() => {
    context.alertVisible = false;
    context.alertMessage = '';
    context.alertType = '';
  }, 2000);
};

</script>

<style scoped lang="scss">

.checkbox-label {
  margin-bottom: 0 !important;
}

</style>



// WEBPACK FOOTER //
// src/brand/health/layouts/nutritionalPlan/NutritionalPlanPrototypeTester.vue